import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare, X, Send, Sparkles, RotateCcw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useData } from '../../lib/DataContext.jsx';
import { buildContext, SAMPLE_QUESTIONS } from '../../lib/pageContext.js';

// Tabs where the assistant is available (spec: first 8 tabs).
const ENABLED = new Set(['overview', 'param', 'oecd', 'geo', 'partner', 'region', 'budget', 'results']);

export default function ChatPanel() {
  const loc = useLocation();
  const pageId = loc.pathname.replace('/', '') || 'overview';
  const enabled = ENABLED.has(pageId);

  const { data, agg, attainment, resultsChain, beneficiaries, filters, filtered } = useData();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [turns, setTurns] = useState([]); // {role, text}
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    // Auto-close/reset when navigating away
    if (!enabled) setOpen(false);
    setTurns([]); setSessionId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [turns, streaming]);

  if (!enabled) return null;

  const send = async (text) => {
    const question = (text ?? q).trim();
    if (!question || streaming) return;
    setQ('');
    setTurns((t) => [...t, { role: 'user', text: question }, { role: 'assistant', text: '' }]);
    setStreaming(true);

    const context = buildContext(pageId, { data, agg, attainment, resultsChain, beneficiaries, filters, filtered });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          page: pageId,
          context,
          history: turns,
          session_id: sessionId,
        }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // parse SSE events
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const p of parts) {
          const line = p.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.delta) {
              setTurns((t) => {
                const next = [...t];
                next[next.length - 1] = { role: 'assistant', text: (next[next.length - 1].text || '') + evt.delta };
                return next;
              });
            } else if (evt.session_id) {
              setSessionId(evt.session_id);
            } else if (evt.error) {
              setTurns((t) => {
                const next = [...t];
                next[next.length - 1] = { role: 'assistant', text: `Sorry — ${evt.error}` };
                return next;
              });
            }
          } catch { /* ignore malformed SSE */ }
        }
      }
    } catch (e) {
      setTurns((t) => {
        const next = [...t];
        next[next.length - 1] = { role: 'assistant', text: `Sorry — couldn't reach the assistant (${e.message}).` };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  const samples = SAMPLE_QUESTIONS[pageId] || [];

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-open"
          style={{
            position: 'fixed', right: 22, bottom: 22, zIndex: 90,
            padding: '12px 18px', borderRadius: 999,
            background: 'linear-gradient(135deg, #004c8f, #042a54)',
            color: '#fff', border: 0, cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(4,42,84,0.35)',
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontSize: 13, fontWeight: 700,
          }}
        >
          <Sparkles size={16} /> Chat with data
        </button>
      )}

      {open && (
        <div data-testid="chat-panel" style={{
          position: 'fixed', right: 22, bottom: 22, zIndex: 90,
          width: 400, maxWidth: 'calc(100vw - 32px)', height: 560, maxHeight: '85vh',
          background: 'var(--surface)', border: '1px solid var(--line)',
          borderRadius: 14, boxShadow: 'var(--shadow-lg)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 14px', background: 'linear-gradient(135deg, #042a54, #004c8f)',
            color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={14} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 800 }}>Parivartan Assistant</div>
                <div style={{ fontSize: 10, opacity: .75, textTransform: 'uppercase', letterSpacing: '.09em' }}>Grounded in this page's data</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {turns.length > 0 && (
                <button data-testid="chat-reset" onClick={() => { setTurns([]); setSessionId(null); }}
                  style={btnGhost} title="Reset conversation">
                  <RotateCcw size={13} />
                </button>
              )}
              <button data-testid="chat-close" onClick={() => setOpen(false)} style={btnGhost} title="Close">
                <X size={13} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollerRef} style={{
            flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: 10,
            background: 'var(--surface-2)',
          }}>
            {turns.length === 0 && (
              <>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                  Ask a question about what you see. I'll answer only from this page's data.
                </div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '.09em', marginTop: 6 }}>
                  Sample questions
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {samples.map((s, i) => (
                    <button key={i} onClick={() => send(s)} data-testid={`chat-sample-${i}`}
                      style={{
                        textAlign: 'left', padding: '8px 12px',
                        border: '1px solid var(--line)', background: 'var(--surface)',
                        borderRadius: 10, cursor: 'pointer', fontSize: 12.5, color: 'var(--ink-2)',
                        transition: 'all .15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--primary-soft)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line)'; e.currentTarget.style.background = 'var(--surface)'; }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}

            {turns.map((t, i) => (
              <div key={i} data-testid={`chat-turn-${i}`} style={{
                alignSelf: t.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                background: t.role === 'user' ? 'var(--primary)' : 'var(--surface)',
                color: t.role === 'user' ? '#fff' : 'var(--ink)',
                border: t.role === 'assistant' ? '1px solid var(--line)' : 'none',
                padding: '8px 12px', borderRadius: 12,
                borderBottomRightRadius: t.role === 'user' ? 4 : 12,
                borderBottomLeftRadius: t.role === 'assistant' ? 4 : 12,
                fontSize: 13, lineHeight: 1.5,
              }}>
                {t.role === 'assistant' ? (
                  t.text ? (
                    <div className="chat-md">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{t.text}</ReactMarkdown>
                    </div>
                  ) : (streaming && i === turns.length - 1 ? <TypingDots /> : null)
                ) : (
                  <span style={{ whiteSpace: 'pre-wrap' }}>{t.text}</span>
                )}
              </div>
            ))}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            style={{ padding: 10, borderTop: '1px solid var(--line)', display: 'flex', gap: 6 }}
          >
            <input
              data-testid="chat-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ask about this page's data…"
              disabled={streaming}
              style={{
                flex: 1, padding: '9px 12px', border: '1px solid var(--line)',
                borderRadius: 999, fontSize: 13, outline: 'none',
                background: streaming ? 'var(--line-2)' : 'var(--surface)',
              }}
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={streaming || !q.trim()}
              style={{
                width: 36, height: 36, borderRadius: '50%',
                background: streaming || !q.trim() ? 'var(--line-2)' : 'var(--primary)',
                color: '#fff', border: 0,
                cursor: streaming || !q.trim() ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

const btnGhost = {
  background: '#ffffff22', border: '1px solid #ffffff44', color: '#fff',
  padding: 5, borderRadius: 6, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

function TypingDots() {
  return (
    <span style={{ display: 'inline-flex', gap: 3 }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 6, height: 6, borderRadius: '50%', background: 'var(--ink-3)',
          animation: `blink 1.2s ${i * 0.15}s infinite`,
        }} />
      ))}
      <style>{`@keyframes blink { 0%,60%,100%{opacity:.3} 30%{opacity:1} }`}</style>
    </span>
  );
}
